import React from "react";
import { CheckCircle, Edit, Timeline } from "@mui/icons-material";
import { Box } from "@mui/material";

import { useGetImportLayers } from "fond/hooks/useGetImportLayers";
import { GroupLabel } from "fond/project/panels/InputPanel/GroupLabel";
import { MuPowerEditableLayerIds, MuPowerSolverLayerIds } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { StackedNavigationButton, SvgIcon } from "fond/widgets";

const ModificationsPanel: React.FC = () => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const modificationLayers = useGetImportLayers(versionId, [
    ...Object.values(MuPowerEditableLayerIds),
    MuPowerSolverLayerIds.sourceHubs,
    MuPowerSolverLayerIds.primaryBlockers,
    MuPowerSolverLayerIds.secondaryBlockers,
    MuPowerSolverLayerIds.primaryFootprint,
    MuPowerSolverLayerIds.secondaryFootprint,
  ]);
  const hasLayer = (id: string) => !!modificationLayers.get(id)?.layer;

  const undergroundComplete = hasLayer(MuPowerEditableLayerIds.ugPath) && hasLayer(MuPowerEditableLayerIds.ugPoints);
  const leadinsComplete = hasLayer(MuPowerEditableLayerIds.leadins);
  const sourceHubsComplete = hasLayer(MuPowerSolverLayerIds.sourceHubs);
  const blockersComplete = hasLayer(MuPowerSolverLayerIds.primaryBlockers) || hasLayer(MuPowerSolverLayerIds.secondaryBlockers);
  const footprintComplete = hasLayer(MuPowerSolverLayerIds.primaryFootprint) || hasLayer(MuPowerSolverLayerIds.secondaryFootprint);

  const rightAdornment = (complete: boolean) => (complete ? <CheckCircle color="success" sx={{ fontSize: 18 }} /> : undefined);

  return (
    <Box>
      <GroupLabel variant="caption" className="first">
        Edit Base Data
      </GroupLabel>
      <StackedNavigationButton
        icon={<Edit />}
        title="Underground"
        target="modification-muPowerUnderground"
        sx={{ m: 0, mt: 1 }}
        checked={undergroundComplete}
        rightElement={rightAdornment(undergroundComplete)}
      />
      <StackedNavigationButton
        icon={<Timeline />}
        title="Leadins"
        target="modification-muPowerLeadins"
        sx={{ m: 0, mt: 1 }}
        checked={leadinsComplete}
        rightElement={rightAdornment(leadinsComplete)}
      />

      <GroupLabel variant="caption">Edit Input Controls</GroupLabel>
      <StackedNavigationButton
        icon={<SvgIcon icon="points" />}
        title="Source Hubs"
        target={MuPowerSolverLayerIds.sourceHubs}
        sx={{ m: 0, mt: 1 }}
        checked={sourceHubsComplete}
        rightElement={rightAdornment(sourceHubsComplete)}
      />
      <StackedNavigationButton
        icon={<SvgIcon icon="line" />}
        title="Blockers"
        target="mu-blockers"
        sx={{ m: 0, mt: 1 }}
        checked={blockersComplete}
        rightElement={rightAdornment(blockersComplete)}
      />
      <StackedNavigationButton
        icon={<SvgIcon icon="line" />}
        title="Footprint"
        target="mu-footprint"
        sx={{ m: 0, mt: 1 }}
        checked={footprintComplete}
        rightElement={rightAdornment(footprintComplete)}
      />
    </Box>
  );
};

export default ModificationsPanel;
